import { useEffect, useState } from 'react';
import { OrderStatus } from '@/interface/order';
import { useTranslation } from 'react-i18next';
import {
  getInfluencerAccounts,
  type InfluencerAccount
} from '@/apis/influencerAccounts';
import CreatorPackageQuoteFlow, {
  type CreatorPackageQuoteFlowProps
} from './CreatorPackageQuoteFlow';

/** Fetch before initializing quote selection; empty success and failure are distinct. */
export default function CreatorPackageQuoteSession(
  props: CreatorPackageQuoteFlowProps
) {
  const { t } = useTranslation();
  const orderId = props.collaborationPackage.id;
  const needsAccounts =
    props.collaborationPackage.raw?.status === OrderStatus.Invited &&
    props.initialPreOfferResponse?.mode !== 'accept' &&
    props.collaborationPackage.raw?.isMultiChannel === true;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    orderId: string;
    accounts?: InfluencerAccount[];
    failed?: boolean;
  } | null>(null);
  useEffect(() => {
    if (!needsAccounts) return;
    let active = true;
    setState(null);
    getInfluencerAccounts({ strict: true }).then(
      (accounts) => {
        if (active) setState({ orderId, accounts });
      },
      () => {
        if (active) setState({ orderId, failed: true });
      }
    );
    return () => {
      active = false;
    };
  }, [orderId, needsAccounts, attempt]);

  if (!needsAccounts) return <CreatorPackageQuoteFlow {...props} />;
  const current = state?.orderId === orderId ? state : null;
  if (!current?.accounts) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6">
        <p role={current?.failed ? 'alert' : 'status'}>
          {current?.failed
            ? t('multi_platform.quote.accounts_load_failed', {
                defaultValue: 'Unable to load your accounts. Please try again.'
              })
            : t('multi_platform.quote.accounts_loading', {
                defaultValue: 'Loading your accounts...'
              })}
        </p>
        {current?.failed ? (
          <button
            type="button"
            className="text-sm font-semibold text-[#FF5A1F]"
            onClick={() => setAttempt((value) => value + 1)}
          >
            {t('multi_platform.quote.accounts_retry', {
              defaultValue: 'Retry'
            })}
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <CreatorPackageQuoteFlow {...props} influencerAccounts={current.accounts} />
  );
}
